import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, CalendarDays, Users, Building2, AlertTriangle, Percent, Gauge } from "lucide-react";
import { fDate } from "@/lib/format";

const DAY = 86400000;
const money = (n) => `${Number(n || 0).toLocaleString("sv-SE")} kr`;
const parse = (s) => (s ? new Date(`${s}T00:00:00`) : null);
const toYMD = (ts) => { const d = new Date(ts); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const FAR = new Date("2999-12-31T00:00:00");

const ZOOM = {
  week: { days: 56, label: "Vecka", shift: 28 },
  month: { days: 183, label: "Månad", shift: 61 },
  quarter: { days: 366, label: "Kvartal", shift: 122 },
};

const STATUS_STYLE = {
  planned: { bar: "bg-amber-100 border-amber-400 text-amber-900", label: "Planerad" },
  active: { bar: "bg-emerald-100 border-emerald-400 text-emerald-900", label: "Aktiv" },
  ended: { bar: "bg-slate-100 border-slate-300 text-slate-600", label: "Avslutad" },
};
const stStyle = (s) => STATUS_STYLE[s] || STATUS_STYLE.planned;

function anchorFor(zoom) {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  if (zoom === "week") {
    const day = (t.getDay() + 6) % 7;
    t.setDate(t.getDate() - day - 7);
    return t;
  }
  if (zoom === "month") return new Date(t.getFullYear(), t.getMonth() - 1, 1);
  return new Date(t.getFullYear(), Math.floor(t.getMonth() / 3) * 3 - 3, 1);
}

function makeTicks(start, end, zoom) {
  const out = [];
  const total = end - start;
  if (zoom === "week") {
    const d = new Date(start);
    while (d <= end) {
      out.push({ pos: ((d - start) / total) * 100, label: `${d.getDate()}/${d.getMonth() + 1}` });
      d.setDate(d.getDate() + 7);
    }
  } else if (zoom === "month") {
    const d = new Date(start.getFullYear(), start.getMonth(), 1);
    while (d <= end) {
      out.push({ pos: ((d - start) / total) * 100, label: d.toLocaleDateString("sv-SE", { month: "short" }) });
      d.setMonth(d.getMonth() + 1);
    }
  } else {
    const d = new Date(start.getFullYear(), Math.floor(start.getMonth() / 3) * 3, 1);
    while (d <= end) {
      out.push({ pos: ((d - start) / total) * 100, label: `K${Math.floor(d.getMonth() / 3) + 1} -${String(d.getFullYear()).slice(2)}` });
      d.setMonth(d.getMonth() + 3);
    }
  }
  return out.filter((t) => t.pos >= -0.1 && t.pos <= 100.1);
}

function conflictSet(placements) {
  const byEmp = {};
  placements.forEach((p) => {
    if (p.status === "ended") return;
    const s = parse(p.start_date);
    if (!s) return;
    const e = parse(p.end_date) || FAR;
    (byEmp[p.employee_id] ||= []).push({ id: p.id, s, e });
  });
  const conf = new Set();
  Object.values(byEmp).forEach((arr) => {
    for (let i = 0; i < arr.length; i++)
      for (let j = i + 1; j < arr.length; j++)
        if (arr[i].s < arr[j].e && arr[j].s < arr[i].e) {
          conf.add(arr[i].id);
          conf.add(arr[j].id);
        }
  });
  return conf;
}

function assignLanes(items) {
  const sorted = [...items].sort((a, b) => a._s - b._s);
  const laneEnds = [];
  sorted.forEach((it) => {
    let lane = laneEnds.findIndex((end) => end <= it._s);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(it._e);
    } else laneEnds[lane] = it._e;
    it._lane = lane;
  });
  return { items: sorted, lanes: Math.max(1, laneEnds.length) };
}

const covers = (day, ranges) => ranges.some((r) => day >= r.s && day <= r.e);

// Beläggningsgrad per konsult: arbetsdagar (mån–fre) i fönstret
function computeUtil(placements, availability, winStart, winEnd) {
  const workdays = [];
  const d = new Date(winStart);
  while (d <= winEnd) {
    const wd = d.getDay();
    if (wd !== 0 && wd !== 6) workdays.push(d.getTime());
    d.setDate(d.getDate() + 1);
  }
  const avById = {};
  availability.forEach((a) => {
    const s = parse(a.start_date);
    if (!s) return;
    const e = parse(a.end_date) || winEnd;
    (avById[a.employee_id] ||= { available: [], unavailable: [] })[a.kind === "unavailable" ? "unavailable" : "available"].push({ s, e });
  });
  const bookById = {};
  placements.forEach((p) => {
    if (p.status === "ended") return;
    const s = parse(p.start_date);
    if (!s) return;
    const e = parse(p.end_date) || winEnd;
    (bookById[p.employee_id] ||= []).push({ s, e });
  });
  const out = {};
  const emps = new Set([...Object.keys(avById), ...Object.keys(bookById)]);
  emps.forEach((eid) => {
    const av = avById[eid] || { available: [], unavailable: [] };
    const book = bookById[eid] || [];
    let capacity = 0, booked = 0;
    workdays.forEach((ts) => {
      let isCap = av.available.length ? covers(ts, av.available) : true;
      if (covers(ts, av.unavailable)) isCap = false;
      if (!isCap) return;
      capacity += 1;
      if (covers(ts, book)) booked += 1;
    });
    out[eid] = { capacity, booked, free: Math.max(0, capacity - booked), pct: capacity ? Math.round((booked / capacity) * 100) : 0 };
  });
  return out;
}

export default function PlaceringsKalender({ placements = [], availability = [], onReschedule }) {
  const [groupBy, setGroupBy] = useState("employee");
  const [zoom, setZoom] = useState("month");
  const [anchor, setAnchor] = useState(() => anchorFor("month"));
  const [showAvail, setShowAvail] = useState(true);
  const [showPct, setShowPct] = useState(true);
  const [drag, setDrag] = useState(null);
  const [preview, setPreview] = useState(null);

  const winStart = anchor;
  const winEnd = new Date(anchor.getTime() + ZOOM[zoom].days * DAY);
  const total = winEnd - winStart;
  const ws = +winStart, we = +winEnd;
  const ticks = useMemo(() => makeTicks(winStart, winEnd, zoom), [ws, we, zoom]);
  const conflicts = useMemo(() => conflictSet(placements), [placements]);
  const util = useMemo(() => computeUtil(placements, availability, winStart, winEnd), [placements, availability, ws, we]);

  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const nowPos = now >= winStart && now <= winEnd ? ((now - winStart) / total) * 100 : null;

  const setZoomKeep = (z) => { setZoom(z); setAnchor(anchorFor(z)); };
  const nav = (dir) => setAnchor(new Date(anchor.getTime() + dir * ZOOM[zoom].shift * DAY));

  const byEmployee = groupBy === "employee";

  const totalDays = Math.max(1, Math.round(total / DAY));
  const dayAtX = (x, rect) => {
    const frac = Math.min(1, Math.max(0, (x - rect.left) / rect.width));
    return ws + Math.round(frac * totalDays) * DAY;
  };
  const startDrag = (e, p, edge) => {
    e.stopPropagation(); e.preventDefault();
    if (!onReschedule) return;
    const track = e.currentTarget.closest("[data-track]");
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const base = { id: p.id, edge, s: +p._s, e: +p._e };
    setDrag(base);
    setPreview({ id: p.id, s: base.s, e: base.e });
    document.body.style.userSelect = "none";
    const move = (ev) => {
      const d = dayAtX(ev.clientX, rect);
      setPreview(edge === "start"
        ? { id: p.id, s: Math.min(d, base.e - DAY), e: base.e }
        : { id: p.id, s: base.s, e: Math.max(d, base.s + DAY) });
    };
    const up = (ev) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      document.body.style.userSelect = "";
      const d = dayAtX(ev.clientX, rect);
      setDrag(null); setPreview(null);
      const patch = {};
      if (edge === "start") { const ns = Math.min(d, base.e - DAY); if (ns !== base.s) patch.start_date = toYMD(ns); }
      else { const ne = Math.max(d, base.s + DAY); if (ne !== base.e) patch.end_date = toYMD(ne); }
      if (Object.keys(patch).length) onReschedule(p, patch);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const rows = useMemo(() => {
    const groups = {};
    placements.forEach((p) => {
      const s = parse(p.start_date);
      if (!s) return;
      const e = parse(p.end_date) || winEnd;
      if (e < winStart || s > winEnd) return;
      const key = byEmployee ? p.employee_id : p.client_id;
      const label = byEmployee ? p.employee_name : p.client_name;
      (groups[key] ||= { key, label, items: [] }).items.push({ ...p, _s: s, _e: e });
    });
    return Object.values(groups)
      .map((g) => ({ ...g, ...assignLanes(g.items) }))
      .sort((a, b) => (a.label || "").localeCompare(b.label || "", "sv"));
  }, [placements, byEmployee, ws, we]);

  const unscheduled = placements.filter((p) => !p.start_date).length;
  const conflictCount = conflicts.size;

  const clip = (s, e) => {
    const cs = Math.max(+s, ws), ce = Math.min(+e, we);
    return { left: ((cs - ws) / total) * 100, width: Math.max(((ce - cs) / total) * 100, 0.6) };
  };

  // Otillgängliga segment per konsult (markerade av arbetstagaren)
  const unavailByEmp = useMemo(() => {
    const m = {};
    availability.forEach((a) => {
      if (a.kind !== "unavailable") return;
      const s = parse(a.start_date);
      if (!s) return;
      const e = parse(a.end_date) || winEnd;
      if (e < winStart || s > winEnd) return;
      (m[a.employee_id] ||= []).push({ s, e });
    });
    return m;
  }, [availability, ws, we]);

  return (
    <Card className="p-4" data-testid="placerings-kalender">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="mr-auto flex items-center gap-2 text-sm font-semibold">
          <CalendarDays className="h-4 w-4 text-accent" />Tidslinje
        </div>
        <div className="flex overflow-hidden rounded-md border" data-testid="cal-groupby">
          <button onClick={() => setGroupBy("employee")} data-testid="cal-group-employee"
            className={`flex items-center gap-1 px-3 py-1.5 text-xs ${byEmployee ? "bg-accent text-accent-foreground" : "bg-background"}`}>
            <Users className="h-3.5 w-3.5" />Konsult
          </button>
          <button onClick={() => setGroupBy("client")} data-testid="cal-group-client"
            className={`flex items-center gap-1 px-3 py-1.5 text-xs ${!byEmployee ? "bg-accent text-accent-foreground" : "bg-background"}`}>
            <Building2 className="h-3.5 w-3.5" />Kund
          </button>
        </div>
        <div className="flex overflow-hidden rounded-md border" data-testid="cal-zoom">
          {Object.entries(ZOOM).map(([k, v]) => (
            <button key={k} onClick={() => setZoomKeep(k)} data-testid={`cal-zoom-${k}`}
              className={`px-3 py-1.5 text-xs ${zoom === k ? "bg-accent text-accent-foreground" : "bg-background"}`}>{v.label}</button>
          ))}
        </div>
        <button onClick={() => setShowAvail((v) => !v)} data-testid="cal-toggle-avail"
          className={`flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs ${showAvail ? "bg-emerald-600 text-white" : "bg-background"}`}>
          <Gauge className="h-3.5 w-3.5" />Beläggning
        </button>
        <button onClick={() => setShowPct((v) => !v)} data-testid="cal-toggle-pct"
          className={`flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs ${showPct ? "bg-accent text-accent-foreground" : "bg-background"}`}>
          <Percent className="h-3.5 w-3.5" />Procent
        </button>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => nav(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" className="h-8" onClick={() => setAnchor(anchorFor(zoom))} data-testid="cal-today">Idag</Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => nav(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <span>{fDate(winStart.toISOString().slice(0, 10))} – {fDate(winEnd.toISOString().slice(0, 10))}</span>
        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-500" />Aktiv</span>
        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-amber-500" />Planerad</span>
        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-slate-400" />Avslutad</span>
        {showAvail && byEmployee && (
          <>
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-300" />Ledig att sälja</span>
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-slate-300" />Otillgänglig</span>
          </>
        )}
        {conflictCount > 0 && (
          <span className="flex items-center gap-1 font-medium text-red-600" data-testid="cal-conflicts">
            <AlertTriangle className="h-3.5 w-3.5" />{conflictCount} placeringar krockar
          </span>
        )}
        {unscheduled > 0 && <span>{unscheduled} utan startdatum visas ej</span>}
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[640px]">
          <div className="relative ml-44 h-6 border-b">
            {ticks.map((t, i) => (
              <div key={i} className="absolute top-0 flex h-full flex-col items-start" style={{ left: `${t.pos}%` }}>
                <span className="absolute h-full border-l border-dashed border-border" />
                <span className="pl-1 text-[10px] text-muted-foreground">{t.label}</span>
              </div>
            ))}
            {nowPos !== null && (
              <div className="absolute top-0 z-10 h-full border-l-2 border-red-500" style={{ left: `${nowPos}%` }} data-testid="cal-now">
                <span className="absolute left-1 text-[10px] font-medium text-red-500">idag</span>
              </div>
            )}
          </div>

          {rows.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="cal-empty">
              Inga placeringar i det valda tidsspannet.
            </div>
          ) : (
            rows.map((row) => {
              const strip = showAvail && byEmployee;
              const rowH = row.lanes * 30 + 8 + (strip ? 12 : 0);
              const u = byEmployee ? util[row.key] : null;
              const unav = unavailByEmp[row.key] || [];
              return (
                <div key={row.key} className="flex items-stretch border-b last:border-b-0" data-testid={`cal-row-${row.key}`}>
                  <div className="flex w-44 shrink-0 flex-col justify-center pr-2 text-sm font-medium" style={{ minHeight: rowH }}>
                    <span className="truncate">{row.label}</span>
                    {showPct && u && (
                      <span className="text-[11px] font-normal text-muted-foreground" data-testid={`cal-util-${row.key}`}>
                        <span className={u.pct >= 90 ? "text-red-600" : u.pct >= 60 ? "text-emerald-600" : "text-amber-600"}>{u.pct}% belagd</span>
                        {u.free > 0 && <span> · {u.free} lediga dgr</span>}
                      </span>
                    )}
                  </div>
                  <div className="relative flex-1" style={{ minHeight: rowH }} data-track="1">
                    {ticks.map((t, i) => (
                      <span key={i} className="absolute top-0 h-full border-l border-dashed border-border/60" style={{ left: `${t.pos}%` }} />
                    ))}
                    {nowPos !== null && <span className="absolute top-0 z-0 h-full border-l-2 border-red-500/40" style={{ left: `${nowPos}%` }} />}
                    {row.items.map((p) => {
                      const pv = preview && preview.id === p.id ? preview : null;
                      const es = pv ? pv.s : +p._s;
                      const ee = pv ? pv.e : +p._e;
                      const { left, width } = clip(es, ee);
                      const s = stStyle(p.status);
                      const conflict = conflicts.has(p.id);
                      const openEnd = !p.end_date || ee >= we;
                      const dragging = drag && drag.id === p.id;
                      return (
                        <div key={p.id} data-testid={`cal-bar-${p.id}`}
                          title={`${p.employee_name} → ${p.client_name}\n${p.role_title || ""}\n${fDate(toYMD(es))}${p.end_date || pv ? ` – ${fDate(toYMD(ee))}` : " – pågående"}\n${money(p.hourly_rate)}/tim${conflict ? "\n⚠ Krockar med annan placering" : ""}`}
                          className={`group absolute flex items-center gap-1 overflow-hidden rounded-md border px-2 text-xs ${s.bar} ${conflict ? "ring-2 ring-red-500" : ""} ${dragging ? "ring-2 ring-accent" : ""}`}
                          style={{ left: `${left}%`, width: `${width}%`, top: p._lane * 30 + 4, height: 26 }}>
                          {onReschedule && (
                            <span onPointerDown={(e) => startDrag(e, p, "start")} data-testid={`cal-resize-start-${p.id}`}
                              className="absolute left-0 top-0 z-10 h-full w-2 cursor-ew-resize bg-black/0 hover:bg-black/15" />
                          )}
                          {conflict && <AlertTriangle className="h-3 w-3 shrink-0 text-red-600" />}
                          <span className="pointer-events-none truncate">{byEmployee ? p.client_name : p.employee_name}</span>
                          {openEnd && !pv && <ChevronRight className="ml-auto h-3 w-3 shrink-0 opacity-60" />}
                          {onReschedule && (
                            <span onPointerDown={(e) => startDrag(e, p, "end")} data-testid={`cal-resize-end-${p.id}`}
                              className="absolute right-0 top-0 z-10 h-full w-2 cursor-ew-resize bg-black/0 hover:bg-black/15" />
                          )}
                        </div>
                      );
                    })}
                    {strip && (
                      <div className="absolute bottom-1 left-0 right-0 h-2 overflow-hidden rounded bg-emerald-300" title="Grön = ledig att sälja" data-testid={`cal-strip-${row.key}`}>
                        {unav.map((seg, i) => {
                          const c = clip(seg.s, seg.e);
                          return <span key={`u${i}`} className="absolute h-full bg-slate-300" style={{ left: `${c.left}%`, width: `${c.width}%` }} />;
                        })}
                        {row.items.filter((p) => p.status !== "ended").map((p) => {
                          const c = clip(p._s, p._e);
                          return <span key={`b${p.id}`} className="absolute h-full bg-accent" style={{ left: `${c.left}%`, width: `${c.width}%` }} />;
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </Card>
  );
}
